import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { useOwnerApi, commercialError } from '../commercial/useCommercialApi';
import { limitValue, usageLine, primaryLabel, featureStateLabel } from '../commercial/commercialFormat';
import { renderUsage, entitlementValueType, isPlatformManaged } from '../commercial/usageStatus';
import OwnerGovernanceModal from '../commercial/OwnerGovernanceModal';
import TenantCommercialPanel from '../commercial/TenantCommercialPanel';
import RefundGovernancePanel from './RefundGovernancePanel';
import TenantMfaPolicyPanel from './TenantMfaPolicyPanel';
import Tenant360IndustryProfile from './Tenant360IndustryProfile';
import Tenant360LegalPolicy from './Tenant360LegalPolicy';
import Tenant360FleetSummary from './Tenant360FleetSummary';
import { RefreshCw, AlertTriangle, CheckCircle2, Building2 } from 'lucide-react';

/**
 * SACP Increment 3 — Tenant 360 (owner view of one tenant).
 * Primary labels are human names (never raw Mongo/override IDs). Wired to live owner endpoints.
 * Owner mutations (grant/deny/extend-trial/beta) require a reason and show the audit result.
 */
const Tenant360 = ({ tenantRef, onClose }) => {
  const { superAdmin } = useSuperAdminAuth();
  const api = useOwnerApi(superAdmin);

  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('overview');   // controlled so the MFA tab can deep-link to Features
  const [features, setFeatures] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [customServices, setCustomServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // Governed tenant mutation policy (read) + CONTROLLED_TEST window renewal (governed, audited, no auto-renew).
  const [policy, setPolicy] = useState(null);
  const [renew, setRenew] = useState(null);          // { days: 7|14|30|'custom' } | null
  const [renewReason, setRenewReason] = useState('');
  const [renewCustom, setRenewCustom] = useState('');
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewErr, setRenewErr] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, f, i, c, mp] = await Promise.allSettled([
        api.get(`/api/v1/super-admin/commercial/tenants/${encodeURIComponent(tenantRef)}/commercial-summary`),
        api.get(`/api/v1/super-admin/commercial/tenants/${encodeURIComponent(tenantRef)}/threesixty/features`),
        api.get(`/api/v1/super-admin/commercial/tenants/${encodeURIComponent(tenantRef)}/effective-integrations`),
        api.get(`/api/v1/super-admin/commercial/tenants/${encodeURIComponent(tenantRef)}/threesixty/custom-services`),
        api.get(`/api/v1/super-admin/commercial/tenant/${encodeURIComponent(tenantRef)}/mutation-policy`),
      ]);
      if (s.status === 'fulfilled') setSummary(s.value.data); else throw s.reason;
      setFeatures(f.status === 'fulfilled' ? (f.value.data?.features || []) : []);
      setIntegrations(i.status === 'fulfilled' ? i.value.data : null);
      setCustomServices(c.status === 'fulfilled' ? c.value.data : null);
      setPolicy(mp.status === 'fulfilled' ? mp.value.data : null);
    } catch (err) {
      setError(commercialError(err));
    } finally {
      setLoading(false);
    }
  };

  // Generic CONTROLLED_TEST window renewal — SAME state, new expiry only, via the existing governed set route.
  const DEFAULT_RENEW_REASON = 'Renew canonical Servisfy staging acceptance window for full-role/full-workflow end-to-end validation. Staging only; no production tenant mutation.';
  const submitRenew = async () => {
    if (renewBusy) return;
    const reason = renewReason.trim();
    if (!reason) { setRenewErr('A governance reason is required.'); return; }
    let expiresAt;
    if (renew?.days === 'custom') {
      if (!renewCustom) { setRenewErr('Pick a custom expiry date/time.'); return; }
      expiresAt = new Date(renewCustom).toISOString();
    } else {
      expiresAt = new Date(Date.now() + (renew?.days || 30) * 86400000).toISOString();
    }
    setRenewBusy(true); setRenewErr(null);
    try {
      const cur = policy?.policy || {};
      await api.post(`/api/v1/super-admin/commercial/tenant/${encodeURIComponent(tenantRef)}/mutation-policy`, {
        mutation_policy: 'CONTROLLED_TEST',               // renewal preserves the SAME state — no downgrade/upgrade
        expires_at: expiresAt,
        reason,
        acceptance_run_id: cur.acceptance_run_id || undefined,       // preserve existing pin
        allowed_operation_types: cur.allowed_operation_types || undefined,   // do not widen operations
      });
      setRenew(null); setRenewReason(''); setRenewCustom('');
      setToast({ ok: true, msg: 'CONTROLLED_TEST window renewed — tenant is mutable for governed staging acceptance.' });
      await load();
    } catch (e) {
      setRenewErr(commercialError(e));
    } finally {
      setRenewBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantRef]);

  // Owner mutations (grant/deny/numeric-override/extend-trial) run through a type-safe, schema-driven
  // modal (OwnerGovernanceModal) — never a native prompt(). Each submit calls a real mounted owner
  // endpoint, persists, bumps the entitlement version, writes audit, and re-reads effective state.
  const [gov, setGov] = useState(null);   // { mode, record, usage, betaAction } | null
  const openGov = (mode, record, usage, betaAction) => setGov({ mode, record, usage, betaAction });

  // GOVERNED REPLACEMENT — resolve the ACTIVE incumbent override for a numeric limit_key from the summary the
  // page already loaded (`overrides_display` is every active override for this tenant, each carrying id/type/
  // limit_key). When one exists, the modal must use the atomic replace primitive rather than creating a second
  // active authority. Ordering mirrors the backend resolver exactly — newest (created_at, id) wins — so the UI
  // targets the same document the server considers current, even against legacy duplicate data.
  const incumbentOverrideIdFor = (record) => {
    const key = record?.limit_key || record?.feature_key || record?.key;
    if (!key) return undefined;
    const rows = (summary?.overrides_display || [])
      .filter((o) => o && o.active !== false && o.type === 'limit_override' && o.limit_key === key)
      .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''))
                      || String(a.id || '').localeCompare(String(b.id || '')));
    return rows.length ? rows[rows.length - 1].id : undefined;
  };

  const Pill = ({ children, tone = 'gray' }) => {
    const tones = { green: 'bg-green-100 text-green-800', gray: 'bg-gray-100 text-gray-600', amber: 'bg-amber-100 text-amber-800', purple: 'bg-purple-100 text-purple-800', blue: 'bg-blue-100 text-blue-800', red: 'bg-red-100 text-red-800' };
    return <Badge className={tones[tone]}>{children}</Badge>;
  };

  // D3: never fall back to the raw business_id as the tenant name (it is shown in its own labelled row).
  const tenantName = primaryLabel(summary?.tenant_display_name, 'Unknown business');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{loading ? '…' : tenantName}</h2>
            <p className="text-sm text-gray-500">Business ID: <span className="font-mono">{summary?.business_id || tenantRef}</span></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          {onClose && <Button onClick={onClose} variant="ghost">Close</Button>}
        </div>
      </div>

      {error && <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200" role="alert"><AlertTriangle className="h-5 w-5 text-red-600" /><p className="text-sm text-red-700">{error}</p></div>}
      {toast && <div className={`flex items-center gap-2 p-3 rounded-lg border ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}<span className="text-sm">{toast.msg}</span></div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="commercial">Commercial</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="custom">Custom Services</TabsTrigger>
          <TabsTrigger value="profile">Industry Profile</TabsTrigger>
          {/* Z13 — a SIBLING of Industry Profile, never a child of it. The two governed dimensions
              vary independently, and nesting one inside the other in the UI would teach the operator
              the opposite of how they actually work. */}
          <TabsTrigger value="legal-policy">Legal Policy</TabsTrigger>
          <TabsTrigger value="governance">Governance</TabsTrigger>
          <TabsTrigger value="mfa">MFA</TabsTrigger>
        </TabsList>

        {/* COMMERCIAL — GOV13-A3 governed subscription / add-ons / requests / wallets / audit.
            Actions render strictly from backend allowed_actions; writes are gated OFF until the release. */}
        <TabsContent value="commercial" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Commercial</CardTitle></CardHeader>
            <CardContent>
              <TenantCommercialPanel api={api} tenantRef={summary?.business_id || tenantRef} />
            </CardContent>
          </Card>
          {/* Domain A — tenant-specific SuperAdmin refund authority (request→review→approve→execute→reconcile) */}
          <div className="mt-4">
            <RefundGovernancePanel api={api} tenantRef={summary?.business_id || tenantRef} />
          </div>
        </TabsContent>

        {/* DR-017: MFA is a Features capability. SuperAdmin GRANTS/DENIES it on the Features tab
            (normal Grant/Deny workflow); tenant Admin/IT then manage the policy in Business Settings.
            This MFA tab is a read-only entitlement + adoption summary with a link to Features. */}
        <TabsContent value="profile" className="pt-4">
          <Tenant360IndustryProfile
            api={api}
            tenantRef={summary?.business_id || tenantRef}
            sector={summary?.sector}
            subSector={summary?.sub_sector}
          />
          {/* GOV-7 E-5 — read-only fleet governance visibility (counts + source labels; NO fleet CRUD). */}
          <div className="mt-4">
            <Tenant360FleetSummary api={api} tenantRef={summary?.business_id || tenantRef} />
          </div>
        </TabsContent>

        {/* GOVERNANCE — governed tenant mutation policy (CONTROLLED_TEST window view + renew). Generic per tenant. */}
        {/* Z13 — per-tenant legal/jurisdiction ASSIGNMENT only. Authoring (values, sources,
            review, publication) lives on the global Legal / Jurisdiction Policies surface; this tab
            selects a published version, records the custodian applicability confirmation, and
            re-reads canonical state. It writes no policy content and no industry pin. */}
        <TabsContent value="legal-policy" className="pt-4">
          {/* The capability row comes from the SAME canonical projection the Features tab renders
              (/threesixty/features -> capabilities.business_plane_features). Passing it down rather
              than refetching keeps ONE availability authority: Commercial Control decides whether
              retention governance is released, and this panel only displays that answer. */}
          <Tenant360LegalPolicy
            api={api}
            tenantRef={summary?.business_id || tenantRef}
            capabilityFeature={(features || []).find((f) => f.feature_key === 'retention_governance') || null}
          />
        </TabsContent>

        <TabsContent value="governance" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Governed mutation policy</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : !policy ? <p className="text-sm text-gray-500">No policy data.</p>
                : (() => {
                  const pv = policy.policy || {};
                  const state = pv.mutation_policy || policy.effective_state || 'NORMAL';
                  const isCT = state === 'CONTROLLED_TEST';
                  const exp = pv.expires_at;
                  const msLeft = exp ? (new Date(exp).getTime() - Date.now()) : null;
                  const daysLeft = msLeft == null ? null : Math.ceil(msLeft / 86400000);
                  const expired = msLeft != null && msLeft <= 0;
                  const soon = !expired && daysLeft != null && daysLeft <= 7;
                  // Backend `mutations_allowed_now` is the AUTHORITY; the day counter is display only.
                  const allowedNow = !!policy.mutations_allowed_now;
                  const statusLabel = allowedNow
                    ? (exp ? `Active · ${soon ? 'expiring' : 'expires'} in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : 'Active')
                    : (expired ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago` : 'Blocked by governance');
                  const tone = allowedNow ? (soon ? 'amber' : 'green') : 'red';
                  const Row = ({ k, v, mono }) => (
                    <div className="py-0.5"><span className="text-gray-500">{k}: </span><span className={mono ? 'font-mono text-xs' : 'font-medium'}>{v ?? '—'}</span></div>
                  );
                  return (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <Pill tone={tone}>{state} · {statusLabel}</Pill>
                        {isCT && (
                          <Button size="sm" variant="outline"
                            onClick={() => { setRenew({ days: 30 }); setRenewReason(DEFAULT_RENEW_REASON); setRenewErr(null); }}>
                            Extend test window
                          </Button>
                        )}
                      </div>
                      {isCT && expired && (
                        <p className="text-xs text-red-700">
                          <b>CONTROLLED_TEST — Expired.</b> Mutating tenant operations are currently blocked by governance
                          (HTTP&nbsp;423 <span className="font-mono">CONTROLLED_TEST_EXPIRED</span>). This is the fail-closed
                          protection working as intended — renew the test window to continue governed staging acceptance.
                        </p>
                      )}
                      {isCT && !expired && soon && (
                        <p className="text-xs text-amber-700">Expiring soon — renew the window before it lapses to avoid a 423 on the next mutation.</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 text-sm">
                        <Row k="Mutations allowed now" v={String(allowedNow)} />
                        <Row k="Version" v={pv.version} />
                        <Row k="Effective from" v={pv.effective_from} />
                        <Row k="Expires" v={exp} />
                        <Row k="Environment" v={pv.environment} />
                        <Row k="Acceptance run id" v={pv.acceptance_run_id} mono />
                        <Row k="Updated at" v={pv.updated_at} />
                        <Row k="Approved by" v={pv.approved_by} />
                        <div className="md:col-span-2"><Row k="Allowed operations" v={(pv.allowed_operation_types || []).join(', ') || '—'} /></div>
                        <div className="md:col-span-2"><Row k="Reason" v={pv.reason} /></div>
                      </div>
                      <p className="text-xs text-gray-400">
                        CONTROLLED_TEST windows are time-bounded and fail closed on expiry (governance safety). Renewal
                        preserves the same policy state, requires a reason, and is audited. No automatic renewal.
                      </p>
                    </div>
                  );
                })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mfa" className="pt-4">
          <TenantMfaPolicyPanel businessId={summary?.business_id || tenantRef}
            onManageFeature={() => setTab('features')} />
        </TabsContent>

        {/* OVERVIEW — the FJON display contract */}
        <TabsContent value="overview" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Commercial Summary</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p> : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <Row k="Business" v={tenantName} />
                  <Row k="Business ID" v={<span className="font-mono">{summary?.business_id || tenantRef}</span>} />
                  <Row k="Plan" v={summary?.plan_display_name || summary?.plan?.plan_display_name || '—'} />
                  <Row k="Plan version" v={summary?.plan_version_label || summary?.plan?.plan_version_label || '—'} />
                  <Row k="Status" v={<Pill tone={summary?.status_label === 'Grandfathered' ? 'purple' : 'green'}>{summary?.status_label || summary?.plan?.status_label || 'Active'}</Pill>} />
                  {/* B1: effective allowance lives on active_slots_limit (grandfathered = "unlimited"),
                      not on a base-plan `limits` object; source comes from the override's source_label. */}
                  <Row k="Integration allowance" v={limitValue(summary?.active_slots_limit)} />
                  <Row k="Allowance source" v={summary?.overrides_display?.find?.((o) => o.limit_key === 'active_integrations')?.source_label || 'Plan'} />
                  <Row k="Effective staff allowance" v={limitValue(summary?.usage?.max_staff?.limit)} />
                  <Row k="Effective storage" v={summary?.usage?.storage_gb?.limit != null ? `${limitValue(summary.usage.storage_gb.limit)} GB` : '—'} />
                  <Row k="Billing frequency" v={summary?.billing_frequency || 'monthly'} />
                </dl>
              )}
              {/* Canonical usage (same resolver Business consumes). Uncalculated metrics render an
                  honest state, never a false zero. */}
              {!loading && summary?.usage_canonical && (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Usage
                    {summary.usage_calculated_at && <span className="ml-2 font-normal normal-case text-gray-400">as of {summary.usage_calculated_at}</span>}
                  </p>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                    {[['Active staff', 'max_staff'], ['Integration slots', 'active_integrations'],
                      ['AI resolutions', 'monthly_ai_resolutions'], ['Engagements', 'monthly_engagements'],
                      ['Storage', 'storage_gb']].map(([label, key]) => (
                      <Row key={key} k={label} v={renderUsage(summary.usage_canonical[key], { unit: key === 'storage_gb' ? 'GB' : undefined })} />
                    ))}
                  </dl>
                </div>
              )}
              {/* Type-safe owner actions. Numeric-limit overrides open the numeric form (never a
                  bare grant/deny); trial extension opens its own form. All persist + audit server-side. */}
              {!loading && (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Owner actions</p>
                  <div className="flex flex-wrap gap-2">
                    {[['Override staff allowance', 'max_staff'], ['Override integration slots', 'active_integrations'],
                      ['Override storage', 'storage_gb']].map(([label, key]) => (
                      <Button key={key} size="sm" variant="outline"
                        onClick={() => openGov('numeric', { limit_key: key, feature_display_name: label.replace('Override ', '') }, summary?.usage_canonical?.[key])}>
                        {label}
                      </Button>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => openGov('trial', { feature_display_name: 'Trial' })}>
                      Extend trial
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Per-feature Grant / Deny is available on the Features tab.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FEATURES — activation state per feature */}
        <TabsContent value="features" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Features</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : features.length === 0 ? <p className="text-sm text-gray-500">No features.</p>
                : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>{['Feature', 'Category', 'Business State', 'Private beta', 'Customer', 'Source', 'Owner action'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {features.map((f) => {
                        // Platform-managed (all_registered_businesses, e.g. SSU) is delivered to every Business and
                        // is NOT tenant-entitlement-gated → truthful "Available / read-only", never Grant/Deny.
                        const platformManaged = isPlatformManaged(f);
                        const st = platformManaged ? { tone: 'green', label: 'Available' } : featureStateLabel(f.business_plane_state);
                        const numeric = entitlementValueType(f) === 'numeric_limit';
                        // PRIVATE BETA — two DIFFERENT facts, read from the backend projection and never
                        // conflated: `release_state` says the CAPABILITY is in private beta, `beta_entitled`
                        // says whether THIS tenant was admitted to it. They move independently, so inferring
                        // either one from the other is wrong in both directions.
                        const inBeta = f.release_state === 'private_beta';
                        const betaGranted = f.beta_entitled === true;
                        const entitleAction = () => openGov(numeric ? 'numeric' : 'boolean', f,
                          numeric ? summary?.usage_canonical?.[f.feature_key] : undefined);
                        return (
                          <tr key={f.feature_key} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-2 font-medium text-gray-900">{primaryLabel(f.feature_display_name, f.feature_key)}</td>
                            <td className="px-3 py-2">{f.catalog_category || f.commercial_category || '—'}</td>
                            <td className="px-3 py-2"><Pill tone={st.tone}>{st.label}</Pill></td>
                            <td className="px-3 py-2">
                              {inBeta
                                ? <Pill tone={betaGranted ? 'green' : 'gray'}>{betaGranted ? 'Granted' : 'Not granted'}</Pill>
                                : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2">{f.customer_visible ? 'Visible' : 'Hidden'}</td>
                            <td className="px-3 py-2 text-gray-600">{platformManaged ? 'Platform' : (f.effective_source || '—')}</td>
                            <td className="px-3 py-2">
                              {platformManaged
                                ? <span className="text-xs text-gray-500">Platform managed · read-only</span>
                                : inBeta ? (
                                  // Both required gates, in the order the backend enforces them. An owner shown
                                  // only "Grant / Deny" on a private-beta row will use it, the row will stay
                                  // PRIVATE_BETA_LOCKED, and nothing on screen explains why — the entitlement
                                  // did take effect, it simply is not the gate doing the refusing.
                                  <div className="space-y-2 min-w-[14rem]">
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase text-gray-500">Step 1 · Private beta</p>
                                      <Button size="sm" variant="outline" className="mt-1"
                                        onClick={() => openGov('beta', f, undefined, betaGranted ? 'revoke' : 'grant')}>
                                        {betaGranted ? 'Revoke private beta' : 'Grant private beta'}
                                      </Button>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase text-gray-500">
                                        Step 2 · Entitlement — {f.entitled ? 'Granted' : 'Not granted'}
                                      </p>
                                      <Button size="sm" variant="outline" className="mt-1" onClick={entitleAction}>
                                        {numeric ? 'Override' : 'Grant / Deny'}
                                      </Button>
                                      {!betaGranted && (
                                        <p className="mt-1 max-w-[14rem] text-[11px] text-amber-700">
                                          Private beta access is also required before this feature can become usable.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={entitleAction}>
                                    {numeric ? 'Override' : 'Grant / Deny'}
                                  </Button>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS */}
        <TabsContent value="integrations" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Integrations</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p> : integrations ? (
                <>
                  <p className="text-sm text-gray-700 mb-3">
                    Connector types: {integrations.connector_types_available} of {integrations.connector_types_total} · Active slots: {usageLine(integrations.active_slots_used, integrations.active_slots_limit)}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(integrations.integrations || []).map((it) => (
                      <div key={it.integration_key} className="flex items-center justify-between py-2 px-3 border rounded">
                        <span className="text-gray-900">{primaryLabel(it.display_name, it.integration_key)}</span>
                        <Pill tone={it.state === 'included' ? 'green' : 'gray'}>{it.state}</Pill>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-3">Tenant credentials are never shown to the owner.</p>
                </>
              ) : <p className="text-sm text-gray-500">No integration data.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CUSTOM SERVICES */}
        <TabsContent value="custom" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Custom Services</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p> : customServices ? (
                <div className="space-y-4 text-sm">
                  <Section title="Requests" rows={(customServices.requests || []).map((r) => `${r.requested_capability || '—'} — ${r.status}`)} />
                  <Section title="Active grants" rows={(customServices.grants || []).map((g) => `${g.service_key} — ${g.status}`)} />
                  <Section title="Offers" rows={(customServices.offers || []).map((o) => `${o.service_key || 'offer'} — ${o.state}`)} />
                </div>
              ) : <p className="text-sm text-gray-500">No custom-service data.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {gov && (
        <OwnerGovernanceModal
          open={!!gov}
          onClose={() => setGov(null)}
          api={api}
          tenantRef={summary?.business_id || tenantRef}
          tenantName={tenantName}
          mode={gov.mode}
          record={gov.record}
          usage={gov.usage}
          incumbentOverrideId={gov.mode === 'numeric' ? incumbentOverrideIdFor(gov.record) : undefined}
          betaAction={gov.betaAction}
          onDone={async () => { setToast({ ok: true, msg: 'Change saved and audited.' }); await load(); }}
        />
      )}

      {/* Extend CONTROLLED_TEST window — governed renewal (same state, new expiry). No auto-renew. */}
      <Dialog open={!!renew} onOpenChange={(o) => { if (!o) { setRenew(null); setRenewErr(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Extend CONTROLLED_TEST window</DialogTitle>
            <DialogDescription>Renewal keeps the policy state = CONTROLLED_TEST; only the expiry changes. Requires a reason and is audited. No automatic renewal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div><span className="text-gray-500">Current policy: </span><span className="font-medium">CONTROLLED_TEST</span> <span className="text-gray-400">(read-only)</span></div>
            <div><span className="text-gray-500">Current expiry: </span>{policy?.policy?.expires_at || '—'} <span className="text-gray-400">(read-only)</span></div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">New window</label>
              <div className="flex flex-wrap gap-2">
                {[['+7 days', 7], ['+14 days', 14], ['+30 days', 30], ['Custom', 'custom']].map(([lbl, val]) => (
                  <button key={lbl} type="button" onClick={() => setRenew({ days: val })}
                    className={`px-3 py-1 text-xs border rounded ${renew?.days === val ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{lbl}</button>
                ))}
              </div>
              {renew?.days === 'custom' && (
                <input type="datetime-local" aria-label="Custom expiry" className="mt-2 border rounded px-2 py-1 text-sm"
                  value={renewCustom} onChange={(e) => setRenewCustom(e.target.value)} />
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Reason (required, audited)</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={3} value={renewReason}
                onChange={(e) => setRenewReason(e.target.value)} placeholder="Why is this test window being renewed?" />
            </div>
            {renewErr && <p className="text-sm text-red-700" role="alert">{renewErr}</p>}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" size="sm" disabled={renewBusy} onClick={() => { setRenew(null); setRenewErr(null); }}>Cancel</Button>
            <Button size="sm" disabled={renewBusy} onClick={submitRenew}>{renewBusy ? 'Renewing…' : 'Renew window'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between py-1 border-b border-gray-100">
    <dt className="text-gray-500">{k}</dt><dd className="text-gray-900 font-medium">{v}</dd>
  </div>
);
const Section = ({ title, rows }) => (
  <div>
    <p className="font-semibold text-gray-800 mb-1">{title}</p>
    {rows.length === 0 ? <p className="text-gray-400">None.</p> : <ul className="list-disc pl-5 text-gray-700">{rows.map((r, i) => <li key={i}>{r}</li>)}</ul>}
  </div>
);

export default Tenant360;
